import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material";
import { SearchModal, SearchResult } from "./common/searchModal";
import {
  FeatureWithName,
  useFeatureSuggestions,
} from "fe-web-components/autocomplete";
import { GenderValue, KahunCategories } from "shared";
import { QualifiedFeature } from "fe-common/graphql";
import { OrganSystemIcon } from "./organSystemIcon";
import { RosebudColorMap } from "./rosebud/rosebudColorMap";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useDiagnosticNavigation } from "./useDiagnosticNavigation";
import { useGetDiseasesInPlan } from "./hooks/useGetDiseasesInPlan";

//region [[ Styles ]]

const DiseaseResult = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  cursor: "pointer",
  "&:hover": {
    background: RosebudColorMap.uglyBlue4,
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]
type SearchDisease = FeatureWithName;
export interface SearchToAddPlanDiseaseProps {
  onDiseaseAdded: (disease: QualifiedFeature) => void;
  openSearchButton?: React.ReactElement;
  gender?: GenderValue;
}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]
const DISORDERS = [KahunCategories.DISORDERS];

export const SearchToAddPlanDisease = ({
  onDiseaseAdded,
  openSearchButton,
  gender,
  ...props
}: SearchToAddPlanDiseaseProps) => {
  const [term, setTerm] = useState<string | null>("");

  const { addDiseaseOpen, setAddDiseaseOpen } = useDiagnosticNavigation();

  useEffect(() => {
    setAddDiseaseOpen(false);
  }, [setAddDiseaseOpen]);

  const diseasesInPlan = useGetDiseasesInPlan();

  const { loading, results } = useFeatureSuggestions(term, DISORDERS, gender);

  const filteredResults = useMemo(() => {
    if (!term || term.trim() === "") return null;
    return results?.filter(
      (r) =>
        !diseasesInPlan?.some((inPlan) => inPlan.disease.id === r.value?.id),
    );
  }, [term, results, diseasesInPlan]);

  const handleSearchTermChanged = useCallback(
    (input: string) => {
      if (!input || input === "") return null;
      setTerm(input);
    },
    [setTerm],
  );

  const handleDiseaseSelected = useCallback(
    (item: SearchDisease) => {
      if (!item) {
        console.error("Add disease to plan - failed to add feature was null");
        return;
      }

      evt({
        category: CATEGORY.AGENT_DD_LIST,
        action: ACTION.ADD_DISEASE_FROM_SEARCH,
        label: `${item.name} - term used: ${term}`,
      });

      onDiseaseAdded(item.value);
    },
    [term, onDiseaseAdded],
  );

  const renderDisease = useCallback(
    (itemToRender: SearchResult<SearchDisease>) => {
      const featureAndName = itemToRender?.value;
      if (!featureAndName) return "oops...";
      return (
        <DiseaseResult key={featureAndName.value?.id}>
          <OrganSystemIcon feature={featureAndName.value} showBackground />
          {featureAndName.name}
        </DiseaseResult>
      );
    },
    [],
  );

  const reportModalClosed = useCallback(() => {
    evt({
      category: CATEGORY.AGENT_DD_LIST,
      action: ACTION.CLOSE_DISEASE_SEARCH,
      label: `last term used: "${term}"`,
    });
    setAddDiseaseOpen(false);
  }, [term, setAddDiseaseOpen]);

  return (
    <SearchModal
      onItemSelected={handleDiseaseSelected}
      onValueChange={handleSearchTermChanged}
      renderResult={renderDisease}
      loading={loading}
      open={addDiseaseOpen}
      placeholder={"Add disease to be included in Plan"}
      results={filteredResults}
      onDone={reportModalClosed}
      hideOpenSearchButton
    />
  );
};
